import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { PosTerminal } from "@/shared/api/pos";

const columnHelper = createColumnHelper<DeskTableFeatures, PosTerminal>();

export type TerminalColumnsOpts = {
  companySlug: string;
  busy: boolean;
  savingName: boolean;
  showBranchPicker: boolean;
  editingId: string | null;
  editName: string;
  setEditName: (v: string) => void;
  onStartEdit: (row: PosTerminal) => void;
  onCancelEdit: () => void;
  onSaveRename: () => void;
  onUnbind: (row: PosTerminal) => void;
  onDeactivate: (row: PosTerminal) => void;
  onReactivate: (row: PosTerminal) => void;
};

export function terminalColumns(opts: TerminalColumnsOpts) {
  return columnHelper.columns([
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 200,
      cell: ({ row }) => {
        const t = row.original;
        const deactivated = t.deactivatedAt != null;
        if (opts.editingId === t.id) {
          return (
            <label className="block text-xs text-[var(--color-muted)]">
              Name
              <Input
                className="mt-1"
                value={opts.editName}
                onChange={(e) => opts.setEditName(e.target.value)}
                autoFocus
                disabled={opts.savingName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    opts.onSaveRename();
                  }
                  if (e.key === "Escape") {
                    opts.onCancelEdit();
                  }
                }}
              />
            </label>
          );
        }
        return (
          <div>
            <div className="font-medium">
              {t.name}
              {deactivated ? (
                <span className="text-xs font-normal text-[var(--color-muted)]">
                  {" "}
                  · deactivated
                </span>
              ) : null}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("code", {
      id: "code",
      header: "Code",
      size: 88,
      cell: (info) => (
        <span className="font-mono text-sm">{info.getValue()}</span>
      ),
    }),
    columnHelper.display({
      id: "meta",
      header: "Device",
      size: 160,
      enableSorting: false,
      cell: ({ row }) => {
        const t = row.original;
        return (
          <span className="text-xs text-[var(--color-muted)]">
            {opts.showBranchPicker && t.branchName
              ? `${t.branchName} · `
              : ""}
            {t.deviceBound ? "device bound" : "unbound"}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 320,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const t = row.original;
        const deactivated = t.deactivatedAt != null;
        const editing = opts.editingId === t.id;
        if (editing) {
          return (
            <div className="flex flex-wrap justify-end gap-1">
              <Button
                type="button"
                size="sm"
                pending={opts.savingName}
                pendingLabel="Saving…"
                disabled={!opts.editName.trim()}
                onClick={() => opts.onSaveRename()}
              >
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={opts.savingName}
                onClick={() => opts.onCancelEdit()}
              >
                Cancel
              </Button>
            </div>
          );
        }
        return (
          <div className="flex flex-wrap justify-end gap-1">
            {!deactivated ? (
              <Button asChild size="sm">
                <Link
                  to={`/c/${opts.companySlug}/b/${t.branchSlug}/pos/t/${t.id}`}
                  className="no-underline"
                >
                  Open
                </Link>
              </Button>
            ) : null}
            {!deactivated ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy || opts.savingName}
                onClick={() => opts.onStartEdit(t)}
              >
                Edit
              </Button>
            ) : null}
            {!deactivated && t.deviceBound ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy}
                onClick={() => opts.onUnbind(t)}
              >
                Unbind
              </Button>
            ) : null}
            {!deactivated ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={opts.busy}
                onClick={() => opts.onDeactivate(t)}
              >
                Deactivate
              </Button>
            ) : null}
            {deactivated ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy}
                onClick={() => opts.onReactivate(t)}
              >
                Reactivate
              </Button>
            ) : null}
          </div>
        );
      },
    }),
  ]);
}
